// Store
import useSiteStore from "../../../store/siteStore.js";

// Components
import { Avatar, Box, Stack, Tab, Tabs, Typography, useMediaQuery } from "@mui/material";
import { PersonOutlined, KeyboardArrowDownRounded } from '@mui/icons-material';
import CoachList from "../../custom/CoachList.js";
import CustomFormControl from "../../custom/FormControl.js";
import { CustomSelect, CustomMenuItem, getMenuProps } from "../../custom/Select.js";
import WeeklyScheduleMobile from "../../custom/WeeklyScheduleMobile.js";

// Hooks
import { useAppTheme } from '../../../hooks/useAppTheme.js';

// Style
import { blue, grey } from "@mui/material/colors/index.js";
import { darkTheme } from '../../../styles/colorSchemes/index.js';

// Other
import { getConsistentAvatarColor } from '../../../utils/index.js';

const ParentHomeMobile = (props) => {
    const { selectedTab, setSelectedTab } = useSiteStore();
    const { athletes, coaches, handleChangeTrainingSession, onChangeSelectedAthlete, onCloseEditTrainingSession, onOpenEditTrainingSession, openEditTrainingSession,
        openWeeklySchedule, selectedAthlete, selectOpen, handleSelectOpen, handleSelectClose } = props;

    // Theme
    const { isDarkMode } = useAppTheme();

    // Tablet breakpoint for inline selector with tabs
    const isTablet = useMediaQuery('(min-width:601px) and (max-width:1000px)');

    const convertBufferToBase64String = (athlete) => {
        if (!athlete?.profile?.picture?.data) return '';
        return btoa(String.fromCharCode(...new Uint8Array(athlete.profile.picture.data)));
    };

    // Style constants for Tabs (Consistent with Coach/NationalCoach)
    const tabSx = {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.85rem',
        minHeight: 36,
        color: isDarkMode ? darkTheme.textSecondary : grey[700],
        px: 2.5,
        py: 0.75,
        minWidth: 90,
        borderRadius: '18px',
        border: `1.5px solid ${isDarkMode ? darkTheme.divider : grey[300]}`,
        backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
        transition: 'all 0.3s ease',
        '&:hover': {
            color: isDarkMode ? darkTheme.accent : blue[600],
            borderColor: isDarkMode ? darkTheme.accent : blue[300],
            backgroundColor: isDarkMode ? 'rgba(255, 152, 0, 0.1)' : blue[50],
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
        '&.Mui-selected': {
            color: isDarkMode ? '#000000' : 'white', // Black text on accent in Dark Mode
            backgroundColor: isDarkMode ? darkTheme.accent : blue[600],
            borderColor: isDarkMode ? darkTheme.accent : blue[600],
            fontWeight: 600,
            boxShadow: isDarkMode ? '0 4px 12px rgba(255, 152, 0, 0.3)' : '0 4px 12px rgba(25, 118, 210, 0.3)',
            '&:hover': {
                backgroundColor: isDarkMode ? darkTheme.accentDark : blue[700],
                borderColor: isDarkMode ? darkTheme.accentDark : blue[700],
                color: isDarkMode ? '#000000' : 'white',
            }
        }
    };

    return (
        <Stack sx={{ ml: '5px', mr: '5px', justifyContent: 'center', alignItems: 'center', minWidth: '0px', width: '1fr' }}>
            <Box sx={{ 
                display: 'flex', 
                width: '100%', 
                minWidth: '0px', 
                justifyContent: isTablet ? 'space-between' : 'center', 
                mt: 1, 
                alignItems: 'center', 
                gap: 1,
                px: isTablet ? 2 : 0,
            }}>
                {/* Left spacer for tablet to balance the selector on right */}
                {isTablet && <Box sx={{ flex: 1 }} />}

                <Tabs 
                    variant="scrollable" 
                    scrollButtons="auto" 
                    value={ selectedTab }
                    onChange={(event, newValue) => setSelectedTab(newValue)}
                    sx={{ 
                        justifyContent: 'center', 
                        minWidth: '0px',
                        overflow: 'visible',
                        '& .MuiTabs-indicator': { 
                            display: 'none'
                        },
                        '& .MuiTabs-flexContainer': {
                            gap: 0.5,
                        },
                        '& .MuiTabs-scroller': {
                            overflow: 'visible !important',
                        }
                    }}>
                        <Tab 
                            label="Wochenplan"
                            sx={tabSx}
                        />
                        <Tab 
                            label="Trainerteam"
                            sx={tabSx}
                        />
                </Tabs>

                {/* Tablet: Inline Athlete Selector on the far right */}
                {isTablet && (
                    <Box sx={{ 
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        minWidth: 0,
                    }}>
                        <CustomSelect 
                            disabled={ athletes.length === 0 } 
                            value={athletes.length === 0 ? '' : (selectedAthlete || '')}
                            onChange={onChangeSelectedAthlete}
                            open={selectOpen}
                            onOpen={handleSelectOpen}
                            onClose={handleSelectClose}
                            IconComponent={KeyboardArrowDownRounded}
                            size="small"
                            displayEmpty
                            isDarkMode={isDarkMode}
                            darkTheme={darkTheme}
                            primaryColor={isDarkMode ? darkTheme.accent : blue[500]}
                            sx={{
                                mb: 1.4,
                                borderRadius: '18px',
                                height: 36,
                                width: 200,
                                bgcolor: isDarkMode ? darkTheme.cardBg : 'white',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderRadius: '18px',
                                    borderWidth: '1.5px',
                                    borderColor: isDarkMode ? darkTheme.divider : grey[300],
                                },
                                '& .MuiSelect-select': {
                                    py: 0.5,
                                    px: 1.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: isDarkMode ? darkTheme.accent : blue[300],
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: isDarkMode ? darkTheme.accent : blue[600],
                                    borderWidth: '1.5px',
                                },
                            }}
                            renderValue={ (selected) => {
                                if (athletes.length === 0 || !selected) {
                                    return (
                                        <Typography variant="body2" sx={{ 
                                            fontStyle: 'italic', 
                                            color: isDarkMode ? darkTheme.textSecondary : grey[500],
                                            fontSize: '0.85rem'
                                        }}>
                                            Kein Athlet
                                        </Typography>
                                    );
                                }
                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <Avatar
                                            src={`data:image/png;base64,${convertBufferToBase64String(selected)}`}
                                            sx={{
                                                width: 22,
                                                height: 22,
                                                bgcolor: getConsistentAvatarColor(selected),
                                                fontSize: '0.7rem',
                                                border: '1px solid rgba(0,0,0,0.1)'
                                            }}
                                        >
                                            {!selected?.profile?.picture?.data && <PersonOutlined sx={{ fontSize: 12 }} />}
                                        </Avatar>
                                        <Typography variant="body2" fontWeight={500} noWrap sx={{ fontSize: '0.85rem', color: isDarkMode ? darkTheme.textPrimary : undefined }}>
                                            { selected.profile.lastName }, { selected.profile.firstName }
                                        </Typography>
                                    </Box>
                                );
                            }}
                            MenuProps={getMenuProps(isDarkMode, darkTheme)}
                        >
                            { athletes.filter(athlete => athlete.profile.lastName !== null)
                                .sort((a, b) => String(a.profile.lastName).localeCompare(String(b.profile.lastName)))
                                .map((athlete, index) => (
                                    <CustomMenuItem 
                                        key={ index } 
                                        value={ athlete }
                                        isDarkMode={isDarkMode}
                                        darkTheme={darkTheme}
                                        primaryColor={isDarkMode ? darkTheme.accent : blue[500]}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                            <Avatar
                                                src={`data:image/png;base64,${convertBufferToBase64String(athlete)}`}
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    bgcolor: getConsistentAvatarColor(athlete),
                                                    fontSize: '0.8rem',
                                                    border: '1px solid rgba(0,0,0,0.1)'
                                                }}
                                            >
                                                {!athlete?.profile?.picture?.data && <PersonOutlined sx={{ fontSize: 16 }} />}
                                            </Avatar>
                                            <Typography variant="body2" fontWeight={500} noWrap>
                                                { athlete.profile.lastName }, { athlete.profile.firstName }
                                            </Typography>
                                        </Box>
                                    </CustomMenuItem>
                                ))
                            }
                        </CustomSelect>
                    </Box>
                )}
            </Box>

            {/* Mobile: Compact Athlete Selector below tabs */}
            {!isTablet && (
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                mb: 2,
                mt: 2,
                px: 2,
                gap: 1.5,
            }}>
                <Typography 
                    variant="body2" 
                    fontWeight={600}
                    sx={{ 
                        color: isDarkMode ? darkTheme.textSecondary : grey[700],
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75
                    }}
                >
                    <PersonOutlined sx={{ fontSize: 18, color: isDarkMode ? darkTheme.accent : blue[600] }} />
                    Athlet:
                </Typography>
                <CustomFormControl sx={{ 
                    flex: 1,
                    maxWidth: 280,
                }} color={isDarkMode ? darkTheme.accent : blue[500]} isDarkMode={isDarkMode} darkTheme={darkTheme}>
                    <CustomSelect 
                        disabled={ athletes.length === 0 } 
                        value={athletes.length === 0 ? '' : (selectedAthlete || '')}
                        onChange={onChangeSelectedAthlete}
                        open={selectOpen}
                        onOpen={handleSelectOpen}
                        onClose={handleSelectClose}
                        IconComponent={KeyboardArrowDownRounded}
                        size="small"
                        displayEmpty
                        isDarkMode={isDarkMode}
                        darkTheme={darkTheme}
                        primaryColor={isDarkMode ? darkTheme.accent : blue[500]}
                        renderValue={ (selected) => {
                            if (athletes.length === 0 || !selected) {
                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.3 }}>
                                        <Typography variant="body2" sx={{ 
                                            fontStyle: 'italic', 
                                            color: isDarkMode ? '#FFFFFF' : grey[500],
                                            opacity: 1 
                                        }}>
                                            Keine Athleten verfügbar
                                        </Typography>
                                    </Box>
                                );
                            }
                            return (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar
                                        src={`data:image/png;base64,${convertBufferToBase64String(selected)}`}
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            bgcolor: getConsistentAvatarColor(selected),
                                            border: '1px solid rgba(0,0,0,0.1)'
                                        }}
                                    >
                                        {!selected?.profile?.picture?.data && <PersonOutlined sx={{ fontSize: 14 }} />}
                                    </Avatar>
                                    <Typography variant="body2" fontWeight={500} color={isDarkMode ? darkTheme.textPrimary : undefined}>
                                        { selected.profile.lastName }, { selected.profile.firstName }
                                    </Typography>
                                </Box>
                            );
                        }}
                        MenuProps={getMenuProps(isDarkMode, darkTheme)}
                    >

                    { athletes.filter(athlete => athlete.profile.lastName !== null)
                        .sort((a, b) => {
                            return String(a.profile.lastName).localeCompare(String(b.profile.lastName))
                        })
                        .map((athlete, index) => (
                            <CustomMenuItem 
                                key={ index } 
                                value={ athlete }
                                isDarkMode={isDarkMode}
                                darkTheme={darkTheme}
                                primaryColor={isDarkMode ? darkTheme.accent : blue[500]}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                    <Avatar
                                        src={`data:image/png;base64,${convertBufferToBase64String(athlete)}`}
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            bgcolor: getConsistentAvatarColor(athlete),
                                            fontSize: '0.875rem',
                                            border: '1px solid rgba(0,0,0,0.1)'
                                        }}
                                    >
                                        {!athlete?.profile?.picture?.data && <PersonOutlined sx={{ fontSize: 18 }} />}
                                    </Avatar>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={500} noWrap>
                                            { athlete.profile.lastName }, { athlete.profile.firstName }
                                        </Typography>
                                        {athlete.squad && (
                                            <Typography variant="caption" color={isDarkMode ? darkTheme.textSecondary : "text.secondary"} noWrap>
                                                {athlete.squad}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </CustomMenuItem>
                        ))
                    }
                </CustomSelect>
            </CustomFormControl>
            </Box>
            )}

            { (selectedAthlete === null || selectedAthlete === undefined) && (
                <Box sx={{ 
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 2,
                    py: 6,
                    minHeight: '300px'
                }}>
                    <Box sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : undefined
                    }}>
                        <PersonOutlined sx={{ fontSize: 48, color: isDarkMode ? darkTheme.textSecondary : grey[400] }} />
                    </Box>
                    <Typography variant='h6' color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'} fontWeight={600} mb={1} sx={{ fontSize: '1.1rem' }}>
                        Kein Athlet ausgewählt
                    </Typography>
                    <Typography variant='body2' color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'} sx={{ textAlign: 'center', maxWidth: 300, fontSize: '0.85rem' }}>
                        Wähle einen Athleten aus, um den Trainingsplan anzuzeigen.
                    </Typography>
                </Box>
            )}

            { selectedTab === 0 && (
                <>
                    { selectedAthlete && (
                        <WeeklyScheduleMobile
                            handleChangeTrainingSession={ handleChangeTrainingSession }
                            isEditable={ false }
                            isFeedbackEditable={ false }
                            onOpenEditTrainingSession={ onOpenEditTrainingSession }
                            openEditTrainingSession={ openEditTrainingSession }
                            onCloseEditTrainingSession={ onCloseEditTrainingSession }
                            open={ openWeeklySchedule }
                            selectedAthlete={ selectedAthlete }
                            deleteFilesAfterDownload={ false } />
                    )}
                </>
            )}
            
            { selectedTab === 1 && coaches !== undefined && (
                <>
                    { selectedAthlete && (
                        <Box sx={{ width: '100%' }}>
                            <Box sx={{ mb: 4 }}/>
                            <CoachList
                                search={ "" }
                                coaches={ coaches }
                                showAthleteAmount={ false }
                                isEditable={ false }/>
                        </Box>
                    )}
                </>
            )}
        </Stack>
    )
}

export default ParentHomeMobile;